// How an evidence driver's STRENGTH is coloured in the report.
//
// One lavender, three weights: full strength for `strong`, and progressively
// lighter tints for `moderate` and `weak`, so the eye ranks a column of drivers
// before reading a word of it.
//
// Derived from the theme's `accent` rather than a literal, because `accent` is
// where each theme puts the Sooth lavender — and the themes that do NOT use
// lavender (dev's gold, mono's white) then shade their own accent instead of
// having a purple injected into a palette that has none.
//
// Unknown or absent strength returns the accent unchanged: an unlabelled driver
// is shown at full weight rather than being quietly demoted to "weak".

/** Tint per tier: 0 keeps the accent, higher is lighter. */
const TINT_BY_STRENGTH = Object.freeze({
  strong: 0,
  moderate: 0.3,
  weak: 0.55,
});

/**
 * The colour for one driver's badge and strength pill.
 *
 * @param {string|null|undefined} strength `"strong"|"moderate"|"weak"`, any case
 * @param {string} accent the theme's accent (`C.accent`)
 * @param {(hex: string, amount: number) => string} tint injected `tintHex`
 * @returns {string} a colour string — the accent itself for `strong`/unknown
 */
export function strengthColor(strength, accent, tint) {
  const key = typeof strength === "string" ? strength.trim().toLowerCase() : "";
  const amount = TINT_BY_STRENGTH[key];
  return amount ? tint(accent, amount) : accent;
}

const STRENGTH_TIER = { low: 1, weak: 1, medium: 2, moderate: 2, high: 3, strong: 3 };

/**
 * A strength rating in the standard report's words — "high" → "strong", "medium" → "moderate",
 * "low" → "weak" — or null when there is none.
 * @param {string|null|undefined} strength
 * @returns {"weak"|"moderate"|"strong"|null}
 */
export function strengthWord(strength) {
  const tier =
    STRENGTH_TIER[
      String(strength || "")
        .trim()
        .toLowerCase()
    ];
  return tier ? ["weak", "moderate", "strong"][tier - 1] : null;
}
